/**
 * Pricing Section - Clean cards with billing toggle
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Sparkles, Zap, Crown } from 'lucide-react';
import { useWebsiteContentOptional } from '../../contexts/WebsiteContentContext';
import { useLanguage } from '../../contexts/LanguageContext';
import EditableElement from '../admin/EditableElement';

const tiers = [
  {
    id: 'free',
    name: 'Free',
    icon: Zap,
    monthlyPrice: 0,
    yearlyPrice: 0,
    description: 'Great for getting started',
    features: ['Track up to 5 items', 'Sale alerts (24-hour delay)', '7-day price history', 'Browser extension', 'Mobile app access'],
    cta: 'Get Started',
    ctaLink: '/signup',
    highlighted: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    icon: Sparkles,
    monthlyPrice: 4.99,
    yearlyPrice: 47.88,
    description: 'For project planners',
    features: ['Track up to 50 items', 'Faster alerts (6-hour delay)', '30-day price history', 'Push notifications', 'Full As-Is scanning access'],
    cta: 'Start Pro Trial',
    ctaLink: '/signup?plan=pro',
    highlighted: true,
  },
  {
    id: 'pro_plus',
    name: 'Pro+',
    icon: Crown,
    monthlyPrice: 9.99,
    yearlyPrice: 95.88,
    description: 'Real-time deal alerts',
    features: ['Unlimited items', 'Real-time sale alerts', '365-day price history', 'General search terms', 'API access', 'Priority support'],
    cta: 'Start Pro+ Trial',
    ctaLink: '/signup?plan=pro_plus',
    highlighted: false,
  },
];

export default function Pricing() {
  const [isYearly, setIsYearly] = useState(false);
  const content = useWebsiteContentOptional();
  const { t } = useLanguage();

  const get = (key: string, fallback: string) =>
    content?.getContent('pricing', key, fallback) ?? fallback;

  const formatPrice = (price: number) => {
    if (price === 0) return '$0';
    return `$${price.toFixed(2)}`;
  };

  const getMonthlyEquivalent = (yearlyPrice: number) => {
    if (yearlyPrice === 0) return '$0';
    return `$${(yearlyPrice / 12).toFixed(2)}`;
  };

  return (
    <section id="pricing" className="py-20 bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <EditableElement section="pricing" field="badge">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-100 rounded-full text-sm font-medium text-primary-700 mb-4">
              {get('badge', 'Pricing')}
            </div>
          </EditableElement>
          <EditableElement section="pricing" field="title">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">
              {get('title', 'Simple,')}
              <span className="text-primary-600">
                {' '}{get('title_highlight', 'transparent pricing')}
              </span>
            </h2>
          </EditableElement>
          <EditableElement section="pricing" field="subtitle">
            <p className="mt-4 text-lg text-gray-600">
              {get('subtitle', 'Start free and upgrade when you need more power.')}
            </p>
          </EditableElement>
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 mb-12">
          <EditableElement section="pricing" field="toggle_monthly">
            <span className={`text-sm font-medium ${!isYearly ? 'text-gray-900' : 'text-gray-500'}`}>
              {get('toggle_monthly', 'Monthly')}
            </span>
          </EditableElement>

          <button
            onClick={() => setIsYearly(!isYearly)}
            className={`relative w-14 h-8 rounded-full transition-colors duration-300 ${isYearly ? 'bg-primary-600' : 'bg-gray-300'}`}
            aria-label={t('pricing.toggleBillingPeriod', 'Toggle billing period')}
          >
            <div
              className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 ${isYearly ? 'translate-x-7' : 'translate-x-1'}`}
            />
          </button>

          <div className="flex items-center gap-2">
            <EditableElement section="pricing" field="toggle_yearly">
              <span className={`text-sm font-medium ${isYearly ? 'text-gray-900' : 'text-gray-500'}`}>
                {get('toggle_yearly', 'Yearly')}
              </span>
            </EditableElement>
            <EditableElement section="pricing" field="toggle_discount">
              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                {get('toggle_discount', 'Save 20%')}
              </span>
            </EditableElement>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-start">
          {tiers.map((tier) => {
            const Icon = tier.icon;
            const name = get(`${tier.id}_name`, tier.name);
            const description = get(`${tier.id}_desc`, tier.description);
            const cta = get(`${tier.id}_cta`, tier.cta);
            const price = isYearly ? tier.yearlyPrice : tier.monthlyPrice;

            return (
              <EditableElement key={tier.id} section="pricing" field={`${tier.id}_name`}>
                <div
                  className={`
                    relative rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1
                    ${tier.highlighted
                      ? 'bg-primary-600 text-white shadow-xl shadow-primary-600/20 scale-105 z-10'
                      : 'bg-white text-gray-900 shadow-lg hover:shadow-xl border border-gray-100'
                    }
                  `}
                >
                  {/* Popular badge */}
                  {tier.highlighted && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <div className="inline-flex items-center gap-1 px-4 py-1.5 bg-accent text-white text-sm font-bold rounded-full shadow-lg">
                        <Sparkles className="w-4 h-4" />
                        {get('popular_badge', 'Most Popular')}
                      </div>
                    </div>
                  )}

                  {/* Tier header */}
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${tier.highlighted ? 'bg-white/20' : 'bg-primary-100'}`}>
                      <Icon className={`w-6 h-6 ${tier.highlighted ? 'text-white' : 'text-primary-600'}`} />
                    </div>
                    <div>
                      <h3 className={`text-xl font-bold ${tier.highlighted ? 'text-white' : 'text-gray-900'}`}>
                        {name}
                      </h3>
                      <p className={`text-sm ${tier.highlighted ? 'text-white/70' : 'text-gray-500'}`}>
                        {description}
                      </p>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold">
                        {isYearly && price > 0 ? getMonthlyEquivalent(price) : formatPrice(price)}
                      </span>
                      <span className={`text-sm ${tier.highlighted ? 'text-white/70' : 'text-gray-500'}`}>
                        {price === 0 ? get('price_forever', 'forever') : get('price_per_month', '/month')}
                      </span>
                    </div>
                    {isYearly && price > 0 && (
                      <p className={`text-sm mt-1 ${tier.highlighted ? 'text-white/70' : 'text-gray-500'}`}>
                        {get('billed_yearly', 'Billed yearly')} ({formatPrice(price)}/year)
                      </p>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${tier.highlighted ? 'bg-white/20' : 'bg-green-100'}`}>
                          <Check className={`w-3 h-3 ${tier.highlighted ? 'text-white' : 'text-green-600'}`} />
                        </div>
                        <span className={`text-sm ${tier.highlighted ? 'text-white/90' : 'text-gray-700'}`}>
                          {get(`${tier.id}_feature${idx + 1}`, feature)}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <Link
                    to={tier.ctaLink}
                    className={`
                      block w-full text-center px-6 py-3.5 rounded-xl font-semibold transition-all duration-300
                      ${tier.highlighted
                        ? 'bg-white text-primary-600 hover:bg-gray-100 shadow-lg'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                      }
                    `}
                  >
                    {cta}
                  </Link>
                </div>
              </EditableElement>
            );
          })}
        </div>

        {/* Trial note */}
        <EditableElement section="pricing" field="note">
          <p className="mt-12 text-center text-sm text-gray-500">
            {get('note', 'All paid plans include a 7-day free trial. No credit card required.')}
          </p>
        </EditableElement>
      </div>
    </section>
  );
}
